import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQueryClient } from 'react-query';
import { ButtonUI, Checkbox, Input, showNotification, validEmailAddress } from 'sonata-react-components';

import { postUserAlias } from '../../../../services/alias.resources';
import './createUserAlias.styles.scss';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

type Props = {
  aliases: UserInterface[];
  onAliasCreated: () => void;
  user: UserInterface;
  onBack: () => void;
};

function CreateUserAlias({ aliases, onAliasCreated, user, onBack }: Props) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sendAliasEmail, setSendAliasEmail] = useState(false);

  const { formatMessage } = useIntl();

  const createAliasToken = {};

  const createMutation = useMutation({
    mutationFn: (newAlias: UserInterface) => postUserAlias(newAlias, createAliasToken),
    mutationKey: ['create-user-alias'],
    onError: () => {
      showNotification(
        'warning',
        formatMessage({
          defaultMessage: 'Alias user could not be created, please try again',
          id: 'menu.userAlias.alert.failCreate'
        })
      );
    },
    onSuccess: () => {
      showNotification(
        'info',
        formatMessage({
          defaultMessage: 'Alias has been created',
          id: 'menu.userAlias.notify.successCreate'
        })
      );

      queryClient.invalidateQueries(['aliases', user]);

      onAliasCreated();
      setName('');
      setEmail('');
      setPassword('');
      setSendAliasEmail(false);
    }
  });

  const handleCreateAlias = () => {
    const isValidEmail = validEmailAddress(email) && !aliases.find(alias => alias.email === email);
    const isValidPassword = password.length >= 6;

    if (!isValidEmail) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage:
            'Alias creation failed, remember alias user emails must exist and be unique. Please try again',
          id: 'menu.userAlias.alert.email'
        })
      );
      return;
    }

    if (!isValidPassword) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage:
            'Alias creation failed, alias user password must be at least six characters long. Please try again',
          id: 'menu.userAlias.alert.password'
        })
      );
      return;
    }

    const { id, ...rest } = user;
    const newAlias = {
      ...rest,
      aliasConfiguration: {
        active: true,
        isAlias: true,
        parentUser: user.id
      },
      email,
      name,
      password,
      sendAliasEmail
    };

    createMutation.mutate(newAlias);
  };

  return (
    <main className="userAliasCreate">
      <div className="userAliasCreate__form">
        <Input
          id={'userAliasCreate__inputName'}
          label={formatMessage({ defaultMessage: 'Name', id: 'menu.userAlias.name' })}
          name="userAliasCreate__inputName"
          onChange={newName => setName(newName as string)}
          placeholder={formatMessage({ defaultMessage: 'My name', id: 'menu.userAlias.name.placeholder' })}
          required
          shape="square"
          type="text"
          value={name}
        />
        <Input
          id={'userAliasCreate__inputEmail'}
          label={formatMessage({ defaultMessage: 'Email', id: 'menu.userAlias.email' })}
          name="userAliasCreate__inputEmail"
          onChange={newEmail => setEmail(newEmail as string)}
          placeholder={formatMessage({ defaultMessage: 'email@gmail.com', id: 'menu.userAlias.email.placeholder' })}
          required
          shape="square"
          type="text"
          value={email}
        />
        <Input
          id={'userAliasCreate__inputPassword'}
          label={formatMessage({ defaultMessage: 'Password', id: 'menu.userAlias.createNew.password' })}
          name="userAliasCreate__inputPassword"
          onChange={newPassword => setPassword(newPassword as string)}
          placeholder="**************"
          required
          shape="square"
          type="password"
          value={password}
        />
      </div>
      <div className="userAliasCreate__checkbox">
        <Checkbox
          active={sendAliasEmail}
          classes="userAliasCreate__sendEmailInfo"
          id={'userAliasCreate__sendEmailInfo'}
          labelText={formatMessage({
            defaultMessage: 'Send an information email to the new alias user',
            id: 'menu.userAlias.createNew.sendEmailInfo'
          })}
          onClickAction={(_, val) => setSendAliasEmail(val)}
        />
      </div>
      <div className="userAliasCreate__footer">
        <ButtonUI
          disabled={createMutation.isLoading}
          onClick={onBack}
          shape="squareRounded"
          size="medium"
          text={formatMessage({ defaultMessage: 'Back', id: 'menu.userAlias.createNew.backButton' })}
          type="secondary"
        />
        <ButtonUI
          className="userAliasCreate__createButton"
          loading={createMutation.isLoading}
          onClick={handleCreateAlias}
          shape="squareRounded"
          size="medium"
          text={formatMessage({ defaultMessage: 'Create', id: 'menu.userAlias.createNew.createButton' })}
          type="primary"
        />
      </div>
    </main>
  );
}

export default CreateUserAlias;
